import { Alert, Button, message } from 'antd'
import { PropsWithChildren, FC, useState } from 'react'

import Text from '~/core/Text'
import { useRequestBusinessAccountInvitesMutation } from '~/gql/graphql'
import logger from '~/lib/logger'

import BusinessAccountJoinTermsModal from './BusinessAccountJoinTermsModal'
import { BannerSubcategory } from './logic'
import { useBannerContext } from './useBannersContext'

interface JoinBannerPropsType {
    onFinishJoin: () => void // when the CTA action has completed
}

/**
 * Text for the banner deviates when isPOFeatureEnabled is true
 */
function getBannerText(
    bannerType: BannerSubcategory,
    businessAccountName: string,
): { messageText: string; headerText: string; testTarget: string } {
    if (bannerType === BannerSubcategory.PO) {
        return {
            headerText: `Join the ${businessAccountName} Business Account on Fictiv!`,
            messageText: `Looks like your organization is using Fictiv & enabled to pay by PO. Join the Business Account to get access.`,
            testTarget: `po-join-button`,
        }
    } else if (bannerType === BannerSubcategory.PREMIUM) {
        return {
            headerText: `Join ${businessAccountName} to access Fictiv Premium`,
            messageText: `Looks like ${businessAccountName} is enabled with Fictiv Premium. Start using Premium with your current account.`,
            testTarget: `premium-join-button`,
        }
    }

    // should not fail unless additional bannerSubcategories are added
    throw new Error('attempted to get banner text without a valid bannerType')
}

/**
 * Component has a CTA for joining a business account as a salesforce contact
 * Expects BannerContext to be set...
 */
export const JoinBanner: FC<PropsWithChildren<JoinBannerPropsType>> = ({
    onFinishJoin,
}) => {
    const {
        businessAccountId,
        userEmail: email,
        businessAccountName,
        bannerSubcategory,
    } = useBannerContext()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [requestBusinessAccountInvites] =
        useRequestBusinessAccountInvitesMutation()

    // Check that everything is defined as expected
    if (!businessAccountName) {
        throw 'Expected business account name to be defined in banner context'
    }

    async function onJoin() {
        const variables = {
            businessAccountId: businessAccountId,
            emails: [email],
        }

        try {
            await requestBusinessAccountInvites({ variables })
            onFinishJoin()
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'My Account Page - failed to request invitation(s) for businessAccount:  ',
                    variables,
                },
            })
            message.error(
                'We encountered an error while sending the invitation request',
            )
        }
    }
    const { messageText, headerText, testTarget } = getBannerText(
        bannerSubcategory,
        businessAccountName,
    )

    const headerElement = <Text>{headerText}</Text>

    const descriptionElement = (
        <Text>
            {messageText}
            <Button onClick={() => setIsModalOpen(true)} type="link">
                View terms.
            </Button>
        </Text>
    )

    return (
        <>
            <Alert
                message={headerElement}
                description={descriptionElement}
                style={{ marginBottom: '16px', alignItems: 'center' }}
                type={'info'}
                action={
                    <Button data-test-target={testTarget} onClick={onJoin}>
                        Join my organization
                    </Button>
                }
                showIcon
                closable={false}
            />
            <BusinessAccountJoinTermsModal
                onClose={() => {
                    setIsModalOpen(false)
                }}
                isOpen={isModalOpen}
            />
        </>
    )
}

export default JoinBanner
